/**
 * Ende-zu-Ende-Verschluesselung der Synchronisierung (T-04, ADR 0006).
 *
 * Aus dem Sync-Passwort wird im Browser mit PBKDF2 ein AES-256-GCM-Schluessel
 * abgeleitet. Er verlaesst das Geraet nie und ist nicht exportierbar. Jeder
 * Eintrag wird mit eigenem Zufalls-IV verschluesselt; `tabelle/id` ist als
 * Zusatzdaten (AAD) gebunden, damit der Server keine Eintraege vertauschen
 * kann. Ohne Passwort ist auf dem Server nichts lesbar - und auch nichts
 * wiederherstellbar.
 */
import { b64u, vonB64u } from '@/lib/utils/base64url';

/** OWASP-Empfehlung fuer PBKDF2-HMAC-SHA256 (2023). */
export const STANDARD_RUNDEN = 600_000;
const PRUEF_TEXT = 'ole-privat-sync';
const enc = new TextEncoder();
const dec = new TextDecoder();

export async function schluesselAbleiten(passwort: string, salz: Uint8Array<ArrayBuffer>, runden: number): Promise<CryptoKey> {
  const basis = await crypto.subtle.importKey('raw', enc.encode(passwort.normalize('NFC')), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salz, iterations: runden, hash: 'SHA-256' },
    basis,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

/** Ergebnis: 12 Byte IV, dann Geheimtext mit Pruefsumme. */
export async function verschluesseln(schluessel: CryptoKey, daten: Uint8Array<ArrayBuffer>, aad: string): Promise<Uint8Array<ArrayBuffer>> {
  const iv = crypto.getRandomValues(new Uint8Array(new ArrayBuffer(12)));
  const geheim = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: enc.encode(aad) }, schluessel, daten));
  const out = new Uint8Array(new ArrayBuffer(12 + geheim.length));
  out.set(iv);
  out.set(geheim, 12);
  return out;
}

/** Wirft, wenn Schluessel, Zusatzdaten oder Inhalt nicht passen. */
export async function entschluesseln(schluessel: CryptoKey, daten: Uint8Array<ArrayBuffer>, aad: string): Promise<Uint8Array<ArrayBuffer>> {
  const klar = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: daten.slice(0, 12), additionalData: enc.encode(aad) }, schluessel, daten.slice(12));
  return new Uint8Array(klar);
}

export async function jsonVerschluesseln(schluessel: CryptoKey, wert: unknown, aad: string): Promise<string> {
  return b64u(await verschluesseln(schluessel, enc.encode(JSON.stringify(wert)), aad));
}

export async function jsonEntschluesseln<T>(schluessel: CryptoKey, text: string, aad: string): Promise<T> {
  return JSON.parse(dec.decode(await entschluesseln(schluessel, vonB64u(text), aad))) as T;
}

export const neuesSalz = (): string => b64u(crypto.getRandomValues(new Uint8Array(new ArrayBuffer(16))));

export const pruefwertErzeugen = (schluessel: CryptoKey) => jsonVerschluesseln(schluessel, PRUEF_TEXT, 'pruefwert');

/** Passt das Passwort zum Pruefwert auf dem Server? */
export async function passwortPasst(schluessel: CryptoKey, pruefwert: string): Promise<boolean> {
  try {
    return (await jsonEntschluesseln<string>(schluessel, pruefwert, 'pruefwert')) === PRUEF_TEXT;
  } catch {
    return false;
  }
}
